import React, { useEffect, useLayoutEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useGrowthBook } from '@growthbook/growthbook-react'

import { useActiveLanguage } from 'hooks/useActiveLanguage'
import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useExperimentalFeatures } from 'hooks/useExperimentalFeatures'
import { useNextStep } from 'hooks/useNextStep'
import { useProgressAnimation } from 'hooks/useProgressAnimation'

import { TPageProps } from 'models/common.model'

import alternativeLoaderBgImage from 'assets/images/alternative-loader-bg-image.webp'
import alternativeLoaderCleanBgImage from 'assets/images/alternative-loader-clean-bg-image.webp'
import speechBubbleTailSvg from 'assets/images/sprite/speech-bubble-tail.svg'

import { replaceHistory } from 'browser-history'
import { TEST_SEGMENTS } from 'root-constants'

import { StyledIntroLoader as S } from './IntroLoader.styles'
import { LoadingProgress } from './components/LoadingProgress'
import { SPEECH_BUBBLE_LONG_TEXT_LENGTH } from './constants'

export const AlternativeAyahLoader: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
}) => {
  const { t } = useTranslation()
  const growthbook = useGrowthBook()
  const { handleChange, setIsAnswersDisabled } = useDelayedContinue()
  const { ayah83Alternative2Variant } = useExperimentalFeatures()
  const { isEnLanguage } = useActiveLanguage()

  const isGrowthBookReady = growthbook?.ready ?? false
  const shouldRenderLoader =
    ayah83Alternative2Variant === TEST_SEGMENTS.VARIANT_B
  const shouldStartLoader = isGrowthBookReady && shouldRenderLoader
  const speechBubbleText = t('onboarding.alternativeAyahLoader.meAt2Am')
  const hasLongSpeechBubbleText =
    speechBubbleText.length > SPEECH_BUBBLE_LONG_TEXT_LENGTH
  const backgroundImage = isEnLanguage
    ? alternativeLoaderBgImage
    : alternativeLoaderCleanBgImage

  const handleContinue = useNextStep({
    pageId,
    question: t('onboarding.alternativeAyahLoader.title'),
    nextPagePath,
  })

  const handleNextStep = () => {
    handleChange('', handleContinue)
    setIsAnswersDisabled(true)
  }

  const { counter, progressAnimationPlayback } = useProgressAnimation({
    duration: 3000,
    callback: handleNextStep,
  })

  useLayoutEffect(() => {
    if (isGrowthBookReady && !shouldRenderLoader) {
      replaceHistory(nextPagePath)
    }
  }, [isGrowthBookReady, nextPagePath, shouldRenderLoader])

  useEffect(() => {
    if (shouldStartLoader) {
      progressAnimationPlayback()
    }
  }, [progressAnimationPlayback, shouldStartLoader])

  if (!isGrowthBookReady || !shouldRenderLoader) {
    return null
  }

  return (
    <S.Root>
      <S.Container $backgroundImage={backgroundImage} $padding="72px 24px 0">
        {!isEnLanguage && (
          <S.BackgroundImageArea>
            <S.SpeechBubble $hasLongText={hasLongSpeechBubbleText}>
              {speechBubbleText}
              <S.SpeechBubbleTail svg={speechBubbleTailSvg} />
            </S.SpeechBubble>
          </S.BackgroundImageArea>
        )}

        <S.Title>
          <Trans i18nKey="onboarding.alternativeAyahLoader.title" />
        </S.Title>
        <S.Description>
          <Trans i18nKey="onboarding.alternativeAyahLoader.subtitle" />
        </S.Description>

        <S.BottomSection $bottomSpacing="24px" $hasBottomGradient>
          <S.Note>
            <Trans i18nKey="onboarding.alternativeAyahLoader.tiktokAlternative" />
          </S.Note>

          <LoadingProgress
            loadingLabel={t('onboarding.introAyah.loadingQuiz')}
            progressLabel={
              <Trans
                i18nKey="onboarding.introAyah.counter"
                values={{ counter }}
              />
            }
            value={counter}
          />
        </S.BottomSection>
      </S.Container>
    </S.Root>
  )
}
